import React, { useState } from 'react';
import ComplianceBadges from '../components/ComplianceBadges';

type ProtocolId = 'sym004-09' | 'herald';

interface LaneData {
  example: string;
  status: string;
}

interface ProtocolLanes {
  lane1: LaneData;
  lane2: LaneData;
  lane3: LaneData;
  lane4: LaneData;
}

const PROTOCOL_DATA: Record<ProtocolId, { label: string; lanes: ProtocolLanes }> = {
  'sym004-09': {
    label: 'Sym004-09',
    lanes: {
      lane1: {
        example: 'Nurse records CBC result: WBC 6.2 \u00d7 10\u2079/L for PAT-001 at C1/D1',
        status: 'Entered',
      },
      lane2: {
        example: 'FHIR Observation POST \u2192 D3RI validates against SOA: CBC expected at C1/D1 \u2713',
        status: 'Validated',
      },
      lane3: {
        example: 'Auto-maps to Rave field: HEMATOLOGY.WBC = 6.2, unit = 10*9/L',
        status: 'Mapped',
      },
      lane4: {
        example: 'No flags \u2014 all values within protocol range',
        status: 'Clear',
      },
    },
  },
  herald: {
    label: 'HERALD',
    lanes: {
      lane1: {
        example: 'Nurse records temperature: 37.2\u00b0C for PAT-003 at V2 Day 29',
        status: 'Entered',
      },
      lane2: {
        example: 'FHIR Observation POST \u2192 D3RI validates: Vital Signs expected at V2 \u2713',
        status: 'Validated',
      },
      lane3: {
        example: 'Auto-maps to Rave field: VITALS.TEMP = 37.2, unit = C',
        status: 'Mapped',
      },
      lane4: {
        example: 'No flags \u2014 within normal range',
        status: 'Clear',
      },
    },
  },
};

const LANE_CONFIG = [
  {
    key: 'lane1' as const,
    number: 1,
    icon: '\ud83d\udccb',
    role: 'Nurse / Site Staff',
    title: 'Nurse enters data in Epic',
    color: '#4299e1',
    bgColor: 'rgba(66, 153, 225, 0.08)',
    borderColor: 'rgba(66, 153, 225, 0.3)',
  },
  {
    key: 'lane2' as const,
    number: 2,
    icon: '\u26a1',
    role: 'D3RI Engine',
    title: 'FHIR trigger fires \u2192 D3RI ingests & validates',
    color: '#48bb78',
    bgColor: 'rgba(72, 187, 120, 0.08)',
    borderColor: 'rgba(72, 187, 120, 0.3)',
  },
  {
    key: 'lane3' as const,
    number: 3,
    icon: '\ud83d\uddc4',
    role: 'EDC (Rave / iMedidata)',
    title: 'D3RI auto-populates EDC (Rave/iMedidata)',
    color: '#9f7aea',
    bgColor: 'rgba(159, 122, 234, 0.08)',
    borderColor: 'rgba(159, 122, 234, 0.3)',
  },
  {
    key: 'lane4' as const,
    number: 4,
    icon: '\ud83d\udea9',
    role: 'Coordinator',
    title: 'Coordinator reviews discrepancy flags',
    color: '#ecc94b',
    bgColor: 'rgba(236, 201, 75, 0.08)',
    borderColor: 'rgba(236, 201, 75, 0.3)',
  },
];

function PulsingDot({ color }: { color: string }) {
  return (
    <span
      style={{
        display: 'inline-block',
        width: 10,
        height: 10,
        borderRadius: '50%',
        backgroundColor: color,
        boxShadow: `0 0 6px ${color}`,
        animation: 'pulse 2s infinite',
      }}
    />
  );
}

function Arrow({ color }: { color: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div
          style={{
            width: 2,
            height: 18,
            backgroundColor: color,
            opacity: 0.5,
          }}
        />
        <div
          style={{
            width: 0,
            height: 0,
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            borderTop: `8px solid ${color}`,
            opacity: 0.5,
          }}
        />
      </div>
    </div>
  );
}

export default function Swimlane() {
  const [protocol, setProtocol] = useState<ProtocolId>('sym004-09');
  const data = PROTOCOL_DATA[protocol];

  return (
    <div style={{ padding: '24px 32px', maxWidth: 960, margin: '0 auto' }}>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>

      {/* Header */}
      <h1 style={{ color: '#e2e8f0', fontSize: 22, fontWeight: 700, margin: 0 }}>
        Epic \u2192 D3RI \u2192 EDC Swimlane
      </h1>
      <p style={{ color: '#a0aec0', fontSize: 14, marginTop: 4, marginBottom: 20 }}>
        Real-time data flow from EHR to validated EDC entry
      </p>

      {/* Protocol selector */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          marginBottom: 24,
          alignItems: 'center',
        }}
      >
        <span style={{ color: '#a0aec0', fontSize: 13, marginRight: 4 }}>Protocol:</span>
        {(['sym004-09', 'herald'] as ProtocolId[]).map((pid) => (
          <button
            key={pid}
            onClick={() => setProtocol(pid)}
            style={{
              padding: '6px 16px',
              borderRadius: 6,
              border: `1px solid ${protocol === pid ? '#4299e1' : '#2d3748'}`,
              backgroundColor: protocol === pid ? 'rgba(66, 153, 225, 0.15)' : '#1a202c',
              color: protocol === pid ? '#63b3ed' : '#a0aec0',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: protocol === pid ? 600 : 400,
            }}
          >
            {PROTOCOL_DATA[pid].label}
          </button>
        ))}
      </div>

      {/* Swimlanes */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {LANE_CONFIG.map((lane, idx) => {
          const laneData = data.lanes[lane.key];
          return (
            <React.Fragment key={lane.key}>
              {idx > 0 && (
                <Arrow color={LANE_CONFIG[idx - 1].color} />
              )}
              <div
                style={{
                  backgroundColor: lane.bgColor,
                  border: `1px solid ${lane.borderColor}`,
                  borderRadius: 8,
                  padding: '14px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                }}
              >
                {/* Left: number + icon + role */}
                <div
                  style={{
                    minWidth: 140,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    flexShrink: 0,
                  }}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      backgroundColor: lane.color,
                      color: '#0d1117',
                      fontWeight: 700,
                      fontSize: 13,
                      flexShrink: 0,
                    }}
                  >
                    {lane.number}
                  </span>
                  <div>
                    <div style={{ fontSize: 16 }}>{lane.icon}</div>
                    <div style={{ color: lane.color, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {lane.role}
                    </div>
                  </div>
                </div>

                {/* Center: title + example */}
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#e2e8f0', fontSize: 13, fontWeight: 600, marginBottom: 2 }}>
                    {lane.title}
                  </div>
                  <div
                    style={{
                      color: '#a0aec0',
                      fontSize: 12,
                      fontFamily: 'monospace',
                      backgroundColor: 'rgba(13, 17, 23, 0.5)',
                      padding: '4px 8px',
                      borderRadius: 4,
                      marginTop: 4,
                    }}
                  >
                    {laneData.example}
                  </div>
                </div>

                {/* Right: status */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    minWidth: 80,
                    justifyContent: 'flex-end',
                    flexShrink: 0,
                  }}
                >
                  <PulsingDot color={lane.color} />
                  <span style={{ color: lane.color, fontSize: 12, fontWeight: 600 }}>
                    {laneData.status}
                  </span>
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* What this replaces */}
      <div
        style={{
          marginTop: 32,
          backgroundColor: '#1a202c',
          border: '1px solid #2d3748',
          borderRadius: 8,
          padding: '16px 20px',
        }}
      >
        <h3 style={{ color: '#ecc94b', fontSize: 14, fontWeight: 700, margin: '0 0 8px 0' }}>
          What this replaces
        </h3>
        <p style={{ color: '#a0aec0', fontSize: 13, lineHeight: 1.6, margin: 0 }}>
          <span style={{ color: '#718096' }}>Traditional flow:</span> Nurse enters in Epic{' '}
          \u2192 prints CRF \u2192 coordinator manually re-enters in EDC \u2192 monitor checks
          for discrepancies.{' '}
          <span style={{ color: '#48bb78', fontWeight: 600 }}>
            D3RI eliminates double-entry and catches errors in real-time.
          </span>
        </p>
      </div>

      {/* Compliance */}
      <div style={{ marginTop: 20 }}>
        <ComplianceBadges badges={['21 CFR Part 11', 'ICH E6(R3)', 'ALCOA']} />
      </div>
    </div>
  );
}
